import { useQuery } from '@tanstack/react-query';
import { KeyRound, LogOut, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { get, post } from '@/lib/api';
import { useAuth, useCompanyUser } from '@/lib/auth';
import { useLocale, useT } from '@/lib/i18n';
import { COMMAND_NAV, COMPANY_NAV } from '@/nav';
import { cn } from './ui';

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  const company = useCompanyUser();
  // a company user sees its own console, only the screens its permissions open
  const items = company ? COMPANY_NAV.filter((it) => (!it.anyOf || it.anyOf.some((k) => company.permissions[k] !== 'none')) && (!it.companyLevel || !company.account)) : COMMAND_NAV;
  return (
    <nav className="flex h-full flex-col bg-side text-white">
      <div className="flex h-14 items-center gap-2.5 border-b border-white/10 px-4">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-brand-600 text-sm font-bold">LP</div>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-semibold">{t('Lottery Platform')}</div>
          <div className="truncate text-[11px] text-white/50">{company ? company.client.nameEn : t('Command Center')}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-3">
        {items.map((it) =>
          it.phase ? (
            <div key={it.to} className="mb-0.5 flex cursor-default items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-white/35" title={`${t('Arrives in phase')} ${it.phase}`} data-testid={`nav-planned-${it.to.slice(1)}`}>
              <it.icon size={15} className="shrink-0" />
              <span className="min-w-0 flex-1 truncate">{t(it.label)}</span>
              <span className="rounded bg-white/10 px-1 text-[9px] font-semibold text-white/45">P{it.phase}</span>
            </div>
          ) : (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === '/'}
              onClick={onNavigate}
              className={({ isActive }) => cn('mb-0.5 flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-white/75 hover:bg-white/5 hover:text-white', isActive && 'bg-white/10 font-medium text-white')}
            >
              <it.icon size={15} className="shrink-0" />
              <span className="min-w-0 flex-1 truncate">{t(it.label)}</span>
            </NavLink>
          ),
        )}
      </div>
    </nav>
  );
}

/** the environment name from the API (ENVIRONMENT_NAME), so nobody mistakes this console for another */
function EnvironmentBadge() {
  const h = useQuery({ queryKey: ['health'], queryFn: () => get<{ environment: string | null }>('/health'), staleTime: Infinity });
  if (!h.data?.environment) return null;
  return <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">{h.data.environment}</span>;
}

export function Shell() {
  const t = useT();
  const { locale, set: setLocale } = useLocale();
  const session = useAuth((s) => s.session);
  const clear = useAuth((s) => s.clear);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);

  const signOut = async () => {
    const token = session?.refreshToken;
    if (token) await post(session && 'kind' in session.user ? '/company/auth/logout' : '/auth/logout', { refreshToken: token }).catch(() => undefined);
    clear();
  };

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 hidden w-60 lg:block">
        <Sidebar />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64">
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-ink-200 bg-white px-4">
          <button className="rounded-md p-1.5 text-ink-600 hover:bg-ink-100 lg:hidden" onClick={() => setOpen((o) => !o)} aria-label={t('Menu')}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
          <EnvironmentBadge />
          <div className="flex-1" />
          <span className="hidden text-sm text-ink-600 sm:inline">{session?.user.displayName}</span>
          <button onClick={() => setLocale(locale === 'km' ? 'en' : 'km')} className="rounded-md border border-ink-200 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100" aria-label={t('Language')}>
            {locale === 'km' ? 'EN' : 'ខ្មែរ'}
          </button>
          <NavLink to="/account" className="rounded-md p-1.5 text-ink-600 hover:bg-ink-100" title={t('Change password')} aria-label={t('Change password')}>
            <KeyRound size={16} />
          </NavLink>
          <button onClick={() => void signOut()} className="rounded-md p-1.5 text-ink-600 hover:bg-ink-100" title={t('Sign out')} aria-label={t('Sign out')}>
            <LogOut size={16} />
          </button>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
